import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import CodeEditor from './CodeEditor';

const TutorialSection = () => {
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const tutorials = [
    {
      id: 1,
      title: 'Building Custom React Hooks',
      category: 'React',
      difficulty: 'Intermediate',
      duration: '15 min read',
      description: 'Learn how to create reusable custom hooks that encapsulate stateful logic and side effects in React applications.',
      tags: ['React', 'Hooks', 'JavaScript', 'State Management'],
      publishDate: '2025-01-15',
      views: 2847,
      likes: 156,
      code: `import { useState, useEffect } from 'react';

// Custom hook for local storage
export const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      setStoredValue(value);
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  };

  return [storedValue, setValue];
};

// Custom hook for API calls
export const useApi = (url) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [url]);

  return { data, loading, error };
};

// Usage example
const MyComponent = () => {
  const [name, setName] = useLocalStorage('userName', '');
  const { data, loading, error } = useApi('/api/users');

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <input 
        value={name} 
        onChange={(e) => setName(e.target.value)}
        placeholder="Enter your name"
      />
      <div>Hello, {name}!</div>
      {data && <div>Users: {data.length}</div>}
    </div>
  );
};`,
      content: `Custom hooks are one of React's most powerful features, allowing you to extract component logic into reusable functions. In this tutorial, we'll explore how to create custom hooks that manage state, handle side effects, and provide clean APIs for your components.

## What are Custom Hooks?

Custom hooks are JavaScript functions that:
- Start with "use" (following React's naming convention)
- Can call other hooks
- Allow you to share stateful logic between components
- Keep components clean and focused

## Creating a Local Storage Hook

Our first example shows how to create a hook that syncs state with localStorage:

\`\`\`javascript
const useLocalStorage = (key, initialValue) => {
  // Implementation details in the code editor
};
\`\`\`

This hook provides a simple API that works just like useState, but persists data across browser sessions.

## Building an API Hook

The useApi hook demonstrates how to encapsulate data fetching logic:

\`\`\`javascript
const useApi = (url) => {
  // Returns { data, loading, error }
};
\`\`\`

## Best Practices

1. **Single Responsibility**: Each hook should have one clear purpose
2. **Error Handling**: Always handle potential errors gracefully
3. **Cleanup**: Use useEffect cleanup functions to prevent memory leaks
4. **TypeScript**: Add proper type definitions for better developer experience
5. **Testing**: Write unit tests for your custom hooks

## Advanced Patterns

- **Compound Hooks**: Hooks that use multiple other hooks
- **Reducer Hooks**: Using useReducer for complex state logic
- **Context Hooks**: Combining hooks with React Context
- **Async Hooks**: Handling asynchronous operations safely

Custom hooks are a game-changer for React development, promoting code reuse and separation of concerns.`
    },
    {
      id: 2,
      title: 'Advanced CSS Grid Techniques',
      category: 'CSS',
      difficulty: 'Advanced',
      duration: '20 min read',
      description: 'Master complex CSS Grid layouts with subgrid, named lines, and responsive design patterns.',
      tags: ['CSS', 'Grid', 'Layout', 'Responsive Design'],
      publishDate: '2025-01-10',
      views: 1923,
      likes: 89,
      code: `.grid-container {
  display: grid;
  grid-template-columns: 
    [sidebar-start] 250px 
    [sidebar-end main-start] 1fr 
    [main-end];
  grid-template-rows: 
    [header-start] 60px 
    [header-end content-start] 1fr 
    [content-end footer-start] 40px 
    [footer-end];
  min-height: 100vh;
  gap: 1rem;
}

.header {
  grid-column: sidebar-start / main-end;
  grid-row: header-start / header-end;
  background: #2563eb;
  color: white;
  display: grid;
  place-items: center;
}

.sidebar {
  grid-column: sidebar-start / sidebar-end;
  grid-row: content-start / footer-start;
  background: #f1f5f9;
  padding: 1rem;
}

.main {
  grid-column: main-start / main-end;
  grid-row: content-start / footer-start;
  padding: 1rem;
  
  /* Nested grid for main content */
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.footer {
  grid-column: sidebar-start / main-end;
  grid-row: footer-start / footer-end;
  background: #374151;
  color: white;
  display: grid;
  place-items: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .grid-container {
    grid-template-columns: 1fr;
    grid-template-rows: 
      [header-start] 60px 
      [header-end content-start] 1fr 
      [content-end footer-start] 40px 
      [footer-end];
  }
  
  .header,
  .footer {
    grid-column: 1 / -1;
  }
  
  .sidebar {
    grid-column: 1 / -1;
    grid-row: content-start / content-end;
    order: 2;
  }
  
  .main {
    grid-column: 1 / -1;
    grid-row: content-start / content-end;
    order: 1;
  }
}

/* Advanced: Subgrid example */
.card-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.card {
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 3;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.card-image {
  grid-row: 1;
  height: 200px;
  background: #d1d5db;
}

.card-content {
  grid-row: 2;
  padding: 1rem;
}

.card-actions {
  grid-row: 3;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
  margin-top: auto;
}`,
      content: `CSS Grid is incredibly powerful, but many developers only scratch the surface. This tutorial dives deep into advanced Grid techniques that will transform how you approach layout design.

## Named Grid Lines

Instead of using numbers, you can name your grid lines for better readability:

\`\`\`css
grid-template-columns: 
  [sidebar-start] 250px 
  [sidebar-end main-start] 1fr 
  [main-end];
\`\`\`

This makes your CSS more semantic and easier to maintain.

## Subgrid (The Game Changer)

Subgrid allows nested grids to participate in their parent's grid:

\`\`\`css
.card {
  display: grid;
  grid-template-rows: subgrid;
  grid-row: span 3;
}
\`\`\`

This ensures consistent alignment across all cards, regardless of content length.

## Advanced Responsive Patterns

### Container Queries with Grid

\`\`\`css
@container (min-width: 400px) {
  .grid-container {
    grid-template-columns: repeat(2, 1fr);
  }
}
\`\`\`

### Intrinsic Web Design

\`\`\`css
grid-template-columns: repeat(auto-fit, minmax(min(300px, 100%), 1fr));
\`\`\`

## Grid Areas for Complex Layouts

\`\`\`css
grid-template-areas:
  "header header header" "sidebar main aside" "footer footer footer";
\`\`\`

## Performance Considerations

1. **Avoid excessive nesting**: Keep grid structures as flat as possible
2. **Use \`contain\` property**: Help the browser optimize rendering
3. **Minimize grid recalculations**: Be careful with dynamic content

## Browser Support and Fallbacks

While Grid has excellent support, always consider fallbacks:

\`\`\`css
.fallback {
  display: flex;
  flex-wrap: wrap;
}

@supports (display: grid) {
  .fallback {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  }
}
\`\`\`

These advanced techniques will help you create more sophisticated, maintainable layouts that adapt beautifully to any screen size.`
    },
    {
      id: 3,
      title: 'TypeScript Generics Deep Dive',
      category: 'TypeScript',
      difficulty: 'Advanced',
      duration: '25 min read',
      description: 'Master TypeScript generics with practical examples, constraints, and advanced patterns.',
      tags: ['TypeScript', 'Generics', 'Types', 'JavaScript'],
      publishDate: '2025-01-05',
      views: 3156,
      likes: 234,
      code: `// Basic Generic Function
function identity<T>(arg: T): T {
  return arg;
}

// Usage
const stringResult = identity<string>("hello"); // Type: string
const numberResult = identity<number>(42);      // Type: number
const autoInferred = identity("world");         // Type: string (inferred)

// Generic Interface
interface ApiResponse<T> {
  data: T;
  status: number;
  message: string;
  timestamp: Date;
}

// Usage with different data types
type UserResponse = ApiResponse<User>;
type ProductResponse = ApiResponse<Product[]>;

// Generic Class with Constraints
interface Identifiable {
  id: string | number;
}

class Repository<T extends Identifiable> {
  private items: T[] = [];

  add(item: T): void {
    this.items.push(item);
  }

  findById(id: string | number): T | undefined {
    return this.items.find(item => item.id === id);
  }

  getAll(): T[] {
    return [...this.items];
  }

  update(id: string | number, updates: Partial<T>): T | null {
    const index = this.items.findIndex(item => item.id === id);
    if (index !== -1) {
      this.items[index] = { ...this.items[index], ...updates };
      return this.items[index];
    }
    return null;
  }
}

// Advanced: Conditional Types
type ApiResult<T> = T extends string 
  ? { message: T } 
  : T extends number 
    ? { count: T } 
    : { data: T };

// Mapped Types with Generics
type Optional<T> = {
  [K in keyof T]?: T[K];
};

type Required<T> = {
  [K in keyof T]-?: T[K];
};

// Utility Type Examples
type Pick<T, K extends keyof T> = {
  [P in K]: T[P];
};

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

// Advanced Generic Patterns
interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'user';
}

// Generic function with multiple type parameters
function createApiClient<TData, TError = Error>(
  baseUrl: string
): {
  get: (endpoint: string) => Promise<ApiResponse<TData>>;
  post: (endpoint: string, data: TData) => Promise<ApiResponse<TData>>;
  handleError: (error: TError) => void;
} {
  return {
    async get(endpoint: string) {
      const response = await fetch(\`\${baseUrl}\${endpoint}\`);
      return response.json();
    },
    async post(endpoint: string, data: TData) {
      const response = await fetch(\`\${baseUrl}\${endpoint}\`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      return response.json();
    },
    handleError(error: TError) {
      console.error('API Error:', error);
    }
  };
}

// Usage
const userApi = createApiClient<User>('/api');
const productApi = createApiClient<Product, CustomError>('/api');`,
      content: `TypeScript generics are one of the most powerful features for creating reusable, type-safe code. This comprehensive guide will take you from basic concepts to advanced patterns.

## Understanding Generics

Generics allow you to create components that work with multiple types while maintaining type safety. Think of them as "type variables" that get filled in when the generic is used.

## Basic Generic Functions

The simplest generic function uses a type parameter:

\`\`\`typescript
function identity<T>(arg: T): T {
  return arg;
}
\`\`\`

The \`<T>\` syntax introduces a type parameter that can be used throughout the function.

## Generic Constraints

Sometimes you need to restrict what types can be used:

\`\`\`typescript
interface Lengthwise {
  length: number;
}

function logLength<T extends Lengthwise>(arg: T): T {
  console.log(arg.length);
  return arg;
}
\`\`\`

## Advanced Patterns

### Conditional Types
\`\`\`typescript
type ApiResult<T> = T extends string 
  ? { message: T } 
  : { data: T };
\`\`\`

### Mapped Types
\`\`\`typescript
type Partial<T> = {
  [P in keyof T]?: T[P];
};
\`\`\`

### Distributive Conditional Types
\`\`\`typescript
type ToArray<T> = T extends any ? T[] : never;
type Result = ToArray<string | number>; // string[] | number[]
\`\`\`

## Real-World Applications

### Repository Pattern
The Repository class shows how generics can create flexible data access layers:

\`\`\`typescript
class Repository<T extends Identifiable> {
  // Implementation provides type safety for any identifiable entity
}
\`\`\`

### API Client
Generic API clients can work with different data types while maintaining type safety:

\`\`\`typescript
const userApi = createApiClient<User>('/api/users');
const productApi = createApiClient<Product>('/api/products');
\`\`\`

## Best Practices

1. **Use meaningful names**: \`T\` is fine for simple cases, but \`TData\`, \`TError\` are clearer
2. **Add constraints when needed**: Don't make generics too broad
3. **Provide defaults**: \`<T = string>\` gives sensible defaults
4. **Use utility types**: Leverage built-in utilities like \`Partial\`, \`Pick\`, \`Omit\`

## Common Pitfalls

- **Over-engineering**: Not everything needs to be generic
- **Poor constraints**: Too loose or too tight constraints
- **Complex conditional types**: Keep them readable and maintainable

Mastering generics will make your TypeScript code more reusable, maintainable, and type-safe.`
    },
    {
      id: 4,
      title: 'Performance Optimization in React',
      category: 'React',
      difficulty: 'Intermediate',
      duration: '18 min read',
      description: 'Learn essential techniques for optimizing React applications including memoization, code splitting, and profiling.',
      tags: ['React', 'Performance', 'Optimization', 'JavaScript'],
      publishDate: '2025-01-01',
      views: 4521,
      likes: 312,
      code: `import React, { memo, useMemo, useCallback, lazy, Suspense } from 'react';

// 1. React.memo for component memoization
const ExpensiveComponent = memo(({ data, onUpdate }) => {
  console.log('ExpensiveComponent rendered');
  
  return (
    <div>
      {data.map(item => (
        <div key={item.id} onClick={() => onUpdate(item.id)}>
          {item.name}
        </div>
      ))}
    </div>
  );
});

// 2. useMemo for expensive calculations
const DataProcessor = ({ items, filter }) => {
  const processedData = useMemo(() => {
    console.log('Processing data...');
    return items
      .filter(item => item.category === filter)
      .map(item => ({
        ...item,
        processed: true,
        score: calculateComplexScore(item)
      }))
      .sort((a, b) => b.score - a.score);
  }, [items, filter]);

  return <div>{/* Render processed data */}</div>;
};

// 3. useCallback for stable function references
const ParentComponent = ({ items }) => {
  const [filter, setFilter] = useState('all');
  
  // Without useCallback, this function is recreated on every render
  const handleItemUpdate = useCallback((itemId) => {
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { ...item, updated: Date.now() } 
          : item
      )
    );
  }, [setItems]);

  return (
    <div>
      <FilterControls filter={filter} onFilterChange={setFilter} />
      <ExpensiveComponent 
        data={items} 
        onUpdate={handleItemUpdate} 
      />
    </div>
  );
};

// 4. Code splitting with lazy loading
const HeavyComponent = lazy(() => import('./HeavyComponent'));
const AdminPanel = lazy(() => import('./AdminPanel'));

const App = () => {
  const [showHeavy, setShowHeavy] = useState(false);
  
  return (
    <div>
      <button onClick={() => setShowHeavy(true)}>
        Load Heavy Component
      </button>
      
      <Suspense fallback={<div>Loading...</div>}>
        {showHeavy && <HeavyComponent />}
      </Suspense>
      
      <Suspense fallback={<AdminLoader />}>
        <AdminPanel />
      </Suspense>
    </div>
  );
};

// 5. Virtual scrolling for large lists
import { FixedSizeList as List } from 'react-window';

const VirtualizedList = ({ items }) => {
  const Row = ({ index, style }) => (
    <div style={style}>
      <ItemComponent item={items[index]} />
    </div>
  );

  return (
    <List
      height={600}
      itemCount={items.length}
      itemSize={50}
      width="100%"
    >
      {Row}
    </List>
  );
};

// 6. Custom hook for debounced values
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// 7. Optimized search component
const SearchComponent = ({ onSearch, data }) => {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);

  const searchResults = useMemo(() => {
    if (!debouncedQuery) return [];
    
    return data.filter(item =>
      item.name.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(debouncedQuery.toLowerCase())
    );
  }, [data, debouncedQuery]);

  useEffect(() => {
    onSearch(searchResults);
  }, [searchResults, onSearch]);

  return (
    <input
      type="text"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      placeholder="Search..."
    />
  );
};

// 8. Performance monitoring
const withPerformanceMonitoring = (WrappedComponent) => {
  return (props) => {
    useEffect(() => {
      const startTime = performance.now();
      
      return () => {
        const endTime = performance.now();
        console.log(\`Component rendered in \${endTime - startTime}ms\`);
      };
    });

    return <WrappedComponent {...props} />;
  };
};`,
      content: `React performance optimization is crucial for creating smooth, responsive applications. This guide covers the most effective techniques for improving your React app's performance.

## Understanding React Rendering

React re-renders components when:
- State changes
- Props change
- Parent component re-renders
- Context value changes

## Memoization Techniques

### React.memo
Prevents unnecessary re-renders of functional components:

\`\`\`jsx
const ExpensiveComponent = memo(({ data, onUpdate }) => {
  // Only re-renders if data or onUpdate changes
});
\`\`\`

### useMemo
Memoizes expensive calculations:

\`\`\`jsx
const expensiveValue = useMemo(() => {
  return heavyCalculation(data);
}, [data]);
\`\`\`

### useCallback
Memoizes function references:

\`\`\`jsx
const handleClick = useCallback(() => {
  doSomething(id);
}, [id]);
\`\`\`

## Code Splitting Strategies

### Route-based Splitting
\`\`\`jsx
const Home = lazy(() => import('./Home'));
const About = lazy(() => import('./About'));
\`\`\`

### Component-based Splitting
\`\`\`jsx
const HeavyModal = lazy(() => import('./HeavyModal'));
\`\`\`

## Handling Large Lists

### Virtual Scrolling
For lists with thousands of items:

\`\`\`jsx
import { FixedSizeList as List } from 'react-window';

<List
  height={600}
  itemCount={items.length}
  itemSize={50}
>
  {Row}
</List>
\`\`\`

### Pagination
Break large datasets into smaller chunks:

\`\`\`jsx
const [currentPage, setCurrentPage] = useState(1);
const itemsPerPage = 20;
const paginatedItems = items.slice(
  (currentPage - 1) * itemsPerPage,
  currentPage * itemsPerPage
);
\`\`\`

## Debouncing User Input

Prevent excessive API calls or calculations:

\`\`\`jsx
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};
\`\`\`

## Image Optimization

### Lazy Loading
\`\`\`jsx
<img 
  src={src} 
  loading="lazy" 
  alt={alt}
/>
\`\`\`

### Responsive Images
\`\`\`jsx
<img
  srcSet={\`
    \${smallImage} 480w,
    \${mediumImage} 800w,
    \${largeImage} 1200w
  \`}
  sizes="(max-width: 480px) 100vw, (max-width: 800px) 50vw, 25vw"
  src={mediumImage}
  alt={alt}
/>
\`\`\`

## Performance Monitoring

### React DevTools Profiler
1. Install React DevTools
2. Use the Profiler tab
3. Record interactions
4. Analyze render times

### Custom Performance Hooks
\`\`\`jsx
const useRenderTime = (componentName) => {
  useEffect(() => {
    const start = performance.now();
    return () => {
      const end = performance.now();
      console.log(\`\${componentName}: \${end - start}ms\`);
    };
  });
};
\`\`\`

## Bundle Analysis

### Webpack Bundle Analyzer
\`\`\`bash
npm install --save-dev webpack-bundle-analyzer
npx webpack-bundle-analyzer build/static/js/*.js
\`\`\`

### Source Map Explorer
\`\`\`bash
npm install --save-dev source-map-explorer
npm run build
npx source-map-explorer 'build/static/js/*.js'
\`\`\`

## Best Practices

1. **Profile before optimizing**: Use React DevTools to identify bottlenecks
2. **Avoid premature optimization**: Don't optimize until you have performance issues
3. **Use production builds**: Always test performance with production builds
4. **Monitor bundle size**: Keep an eye on your bundle size as you add features
5. **Optimize images**: Use appropriate formats and sizes
6. **Implement proper caching**: Use service workers and HTTP caching

Remember: The best optimization is often avoiding unnecessary work altogether. Focus on efficient algorithms and data structures before micro-optimizations.`
    },
    {
      id: 5,
      title: 'Modern JavaScript ES2024 Features',
      category: 'JavaScript',
      difficulty: 'Beginner',
      duration: '12 min read',
      description: 'Explore the latest JavaScript features including top-level await, private fields, and more.',
      tags: ['JavaScript', 'ES2024', 'Modern JS', 'Features'],
      publishDate: '2024-12-28',
      views: 1876,
      likes: 145,
      code: `// 1. Top-level await (ES2022)
// No need to wrap in async function anymore
const userData = await fetch('/api/user').then(r => r.json());
console.log(userData);

// 2. Private fields and methods (ES2022)
class BankAccount {
  // Private fields
  #balance = 0;
  #accountNumber;
  
  // Private method
  #validateAmount(amount) {
    return amount > 0 && typeof amount === 'number';
  }
  
  constructor(accountNumber, initialBalance = 0) {
    this.#accountNumber = accountNumber;
    this.#balance = initialBalance;
  }
  
  deposit(amount) {
    if (this.#validateAmount(amount)) {
      this.#balance += amount;
      return this.#balance;
    }
    throw new Error('Invalid amount');
  }
  
  getBalance() {
    return this.#balance;
  }
  
  // Static private method
  static #generateAccountNumber() {
    return Math.random().toString(36).substr(2, 9);
  }
  
  static createAccount(initialBalance) {
    const accountNumber = this.#generateAccountNumber();
    return new BankAccount(accountNumber, initialBalance);
  }
}

// 3. Array.at() method (ES2022)
const fruits = ['apple', 'banana', 'orange', 'grape'];

// Old way
console.log(fruits[fruits.length - 1]); // 'grape'

// New way - negative indexing
console.log(fruits.at(-1)); // 'grape'
console.log(fruits.at(-2)); // 'orange'

// 4. Object.hasOwn() (ES2022)
const obj = { name: 'John', age: 30 };

// Old way
console.log(obj.hasOwnProperty('name')); // true

// New way - more reliable
console.log(Object.hasOwn(obj, 'name')); // true

// 5. Error.cause (ES2022)
async function fetchUserData(userId) {
  try {
    const response = await fetch(\`/api/users/\${userId}\`);
    if (!response.ok) {
      throw new Error('Failed to fetch user data');
    }
    return await response.json();
  } catch (originalError) {
    // Chain errors with cause
    throw new Error('User data retrieval failed', {
      cause: originalError
    });
  }
}

// 6. RegExp match indices (ES2022)
const text = 'Hello World 2024';
const regex = /(\d{4})/d; // 'd' flag for indices
const match = text.match(regex);

console.log(match[1]); // '2024'
console.log(match.indices[1]); // [12, 16] - start and end positions

// 7. Temporal API (Proposal - Stage 3)
// Note: This is still a proposal, but likely coming soon
/*
import { Temporal } from '@js-temporal/polyfill';

// Better date handling
const now = Temporal.Now.plainDateTimeISO();
const birthday = Temporal.PlainDate.from('1990-05-15');
const age = now.toPlainDate().since(birthday).years;

// Duration calculations
const duration = Temporal.Duration.from({ hours: 2, minutes: 30 });
const futureTime = now.add(duration);
*/

// 8. Pipeline operator (Proposal)
// Note: Still in proposal stage
/*
const result = value
  |> doubleSay
  |> capitalize
  |> exclaim;
*/

// Current alternative with function composition
const pipe = (...fns) => (value) => fns.reduce((acc, fn) => fn(acc), value);

const doubleSay = str => str + ', ' + str;
const capitalize = str => str.toUpperCase();
const exclaim = str => str + '!';

const result = pipe(doubleSay, capitalize, exclaim)('hello');
console.log(result); // 'HELLO, HELLO!'

// 9. Decorators (Proposal - Stage 3)
// Note: Still in proposal, but TypeScript supports them
/*
function logged(target, propertyKey, descriptor) {
  const originalMethod = descriptor.value;
  
  descriptor.value = function(...args) {
    console.log(\`Calling \${propertyKey} with args:\`, args);
    const result = originalMethod.apply(this, args);
    console.log(\`Result:\`, result);
    return result;
  };
  
  return descriptor;
}

class Calculator {
  @logged
  add(a, b) {
    return a + b;
  }
}
*/

// 10. Pattern matching (Proposal)
// Note: Still in early proposal stage
/*
const result = match (value) {
  when Number if value > 0 => 'positive number',
  when Number if value < 0 => 'negative number',
  when 0 => 'zero',
  when String => 'string value',
  when Array => 'array value',
  else => 'unknown type'
};
*/

// Current alternative with switch
function matchValue(value) {
  switch (true) {
    case typeof value === 'number' && value > 0:
      return 'positive number';
    case typeof value === 'number' && value < 0:
      return 'negative number';
    case value === 0:
      return 'zero';
    case typeof value === 'string':
      return 'string value';
    case Array.isArray(value):
      return 'array value';
    default:
      return 'unknown type';
  }
}

// 11. Array grouping methods (ES2024)
const people = [
  { name: 'Alice', age: 25, department: 'Engineering' },
  { name: 'Bob', age: 30, department: 'Marketing' },
  { name: 'Charlie', age: 35, department: 'Engineering' },
  { name: 'Diana', age: 28, department: 'Marketing' }
];

// Group by department
const groupedByDept = people.group(person => person.department);
console.log(groupedByDept);
// {
//   Engineering: [{ name: 'Alice', ... }, { name: 'Charlie', ... }],
//   Marketing: [{ name: 'Bob', ... }, { name: 'Diana', ... }]
// }

// Group to Map
const groupedMap = people.groupToMap(person => person.department);

// 12. Promise.withResolvers() (ES2024)
function createPromiseWithResolvers() {
  const { promise, resolve, reject } = Promise.withResolvers();
  
  // You can now use resolve/reject outside the Promise constructor
  setTimeout(() => {
    resolve('Success!');
  }, 1000);
  
  return promise;
}`,
      content: `JavaScript continues to evolve rapidly, with new features being added regularly. This guide covers the most important recent additions and upcoming features that will shape how we write JavaScript.

## ES2022 Features (Stable)

### Top-level Await
No more wrapping await in async functions at the module level:

\`\`\`javascript
// Before
(async () => {
  const data = await fetch('/api/data');
})();

// Now
const data = await fetch('/api/data');
\`\`\`

### Private Fields and Methods
True privacy in JavaScript classes:

\`\`\`javascript
class MyClass {
  #privateField = 'secret';
  
  #privateMethod() {
    return this.#privateField;
  }
}
\`\`\`

### Array.at() Method
Negative indexing for arrays:

\`\`\`javascript
const arr = [1, 2, 3, 4, 5];
console.log(arr.at(-1)); // 5
console.log(arr.at(-2)); // 4
\`\`\`

### Object.hasOwn()
A more reliable alternative to hasOwnProperty:

\`\`\`javascript
const obj = { prop: 'value' };
console.log(Object.hasOwn(obj, 'prop')); // true
\`\`\`

## ES2024 Features

### Array Grouping
Built-in methods for grouping array elements:

\`\`\`javascript
const items = [
  { category: 'fruit', name: 'apple' },
  { category: 'vegetable', name: 'carrot' },
  { category: 'fruit', name: 'banana' }
];

const grouped = items.group(item => item.category);
// { fruit: [...], vegetable: [...] }
\`\`\`

### Promise.withResolvers()
Get promise resolvers outside the constructor:

\`\`\`javascript
const { promise, resolve, reject } = Promise.withResolvers();

// Use resolve/reject anywhere
setTimeout(() => resolve('Done!'), 1000);
\`\`\`

## Upcoming Features (Proposals)

### Temporal API
A modern replacement for Date:

\`\`\`javascript
// Better date handling (when available)
const now = Temporal.Now.plainDateTimeISO();
const birthday = Temporal.PlainDate.from('1990-05-15');
\`\`\`

### Pipeline Operator
Functional composition made easy:

\`\`\`javascript
// Proposed syntax
const result = value
  |> transform
  |> validate
  |> process;
\`\`\`

### Pattern Matching
Powerful matching syntax:

\`\`\`javascript
// Proposed syntax
const result = match (value) {
  when Number if value > 0 => 'positive',
  when String => 'text',
  else => 'other'
};
\`\`\`

### Decorators
Metadata and behavior modification:

\`\`\`javascript
class MyClass {
  @logged
  @cached
  expensiveMethod() {
    // Method implementation
  }
}
\`\`\`

## Browser Support and Polyfills

### Checking Support
\`\`\`javascript
// Feature detection
if ('at' in Array.prototype) {
  // Use Array.at()
} else {
  // Fallback
}
\`\`\`

### Using Babel
Configure Babel to transpile modern features:

\`\`\`json
{
  "presets": [
    ["@babel/preset-env", {
      "targets": "> 0.25%, not dead"
    }]
  ]
}
\`\`\`

## Best Practices

1. **Progressive Enhancement**: Use feature detection
2. **Transpilation**: Use Babel for older browser support
3. **Stay Updated**: Follow TC39 proposals
4. **Test Thoroughly**: New features may have edge cases
5. **Consider Polyfills**: For critical features in older browsers

## Migration Strategies

### Gradual Adoption
- Start with non-critical code
- Use TypeScript for better tooling
- Set up proper testing

### Team Education
- Share knowledge about new features
- Create coding standards
- Use linting rules to enforce patterns

The JavaScript ecosystem moves fast, but these features represent significant improvements in developer experience and code quality. Stay curious and keep experimenting!`
    }
  ];

  const categories = ['all', 'React', 'CSS', 'TypeScript', 'JavaScript'];
  const difficulties = ['Beginner', 'Intermediate', 'Advanced'];

  const filteredTutorials = tutorials?.filter(tutorial => {
    const matchesSearch = tutorial?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         tutorial?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         tutorial?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || tutorial?.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Beginner': 'text-green-600 bg-green-100',
      'Intermediate': 'text-yellow-600 bg-yellow-100',
      'Advanced': 'text-red-600 bg-red-100'
    };
    return colors?.[difficulty] || 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder="Search tutorials, topics, or technologies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-full"
          />
        </div>
        <div className="flex gap-2">
          {categories?.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
              className="capitalize"
            >
              {category}
            </Button>
          ))}
        </div>
      </div>
      {/* Tutorial Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTutorials?.map((tutorial) => (
          <div
            key={tutorial?.id}
            className="bg-background rounded-lg border border-border p-6 hover:shadow-medium transition-all duration-300 cursor-pointer"
            onClick={() => setSelectedTutorial(selectedTutorial === tutorial?.id ? null : tutorial?.id)}
          >
            {/* Tutorial Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-primary hover:text-accent transition-colors mb-2">
                  {tutorial?.title}
                </h3>
                <div className="flex items-center space-x-3 text-sm text-text-secondary">
                  <span className={`px-2 py-1 rounded-full text-xs ${getDifficultyColor(tutorial?.difficulty)}`}>
                    {tutorial?.difficulty}
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{tutorial?.duration}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="Eye" size={14} />
                    <span>{tutorial?.views?.toLocaleString()}</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <Icon name="Heart" size={14} />
                    <span>{tutorial?.likes}</span>
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="xs"
                iconName={selectedTutorial === tutorial?.id ? "ChevronUp" : "ChevronDown"}
              />
            </div>

            {/* Description */}
            <p className="text-sm text-text-secondary mb-4">
              {tutorial?.description}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {tutorial?.tags?.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs bg-muted text-text-secondary rounded"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Publish Date */}
            <div className="text-xs text-text-secondary">
              Published on {new Date(tutorial.publishDate)?.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>

            {/* Expanded Content */}
            {selectedTutorial === tutorial?.id && (
              <div className="mt-6 pt-6 border-t border-border space-y-6">
                {/* Code Example */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center space-x-2">
                    <Icon name="Code2" size={16} className="text-accent" />
                    <span>Code Example</span>
                  </h4>
                  <CodeEditor
                    title={`${tutorial?.category?.toLowerCase()}-example.${tutorial?.category === 'CSS' ? 'css' : tutorial?.category === 'TypeScript' ? 'ts' : 'js'}`}
                    language={tutorial?.category === 'CSS' ? 'css' : tutorial?.category === 'TypeScript' ? 'typescript' : 'javascript'}
                    initialCode={tutorial?.code}
                    showOutput={false}
                  />
                </div>

                {/* Tutorial Content */}
                <div>
                  <h4 className="font-medium mb-3 flex items-center space-x-2">
                    <Icon name="BookOpen" size={16} className="text-accent" />
                    <span>Tutorial Content</span>
                  </h4>
                  <div className="prose prose-sm max-w-none">
                    <div className="bg-muted/30 rounded-lg p-4 text-sm text-text-secondary whitespace-pre-line">
                      {tutorial?.content}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Heart"
                      iconPosition="left"
                      className="text-text-secondary hover:text-red-500"
                    >
                      Like ({tutorial?.likes})
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Share2"
                      iconPosition="left"
                      className="text-text-secondary"
                    >
                      Share
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Bookmark"
                      iconPosition="left"
                      className="text-text-secondary"
                    >
                      Bookmark
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="right"
                  >
                    Read Full Article
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* No Results */}
      {filteredTutorials?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} className="mx-auto mb-4 text-text-secondary opacity-50" />
          <h3 className="text-lg font-medium mb-2">No tutorials found</h3>
          <p className="text-text-secondary mb-4">
            Try adjusting your search terms or filters
          </p>
          <Button
            variant="outline"
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('all');
            }}
          >
            Clear Filters
          </Button>
        </div>
      )}
      {/* Newsletter Signup */}
      <div className="bg-gradient-to-r from-accent/10 to-primary/10 rounded-lg p-6 text-center">
        <h3 className="text-xl font-semibold mb-2">Stay Updated</h3>
        <p className="text-text-secondary mb-4">
          Get notified when new tutorials and code examples are published
        </p>
        <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
          <Input
            type="email"
            placeholder="Enter your email"
            className="flex-1"
          />
          <Button
            variant="default"
            iconName="Mail"
            iconPosition="left"
            className="bg-accent hover:bg-hover text-accent-foreground"
          >
            Subscribe
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TutorialSection;